import React, { useEffect, useMemo } from 'react';
import BaseModal, {
	BaseModalProps,
} from '../../../Base/Components/BaseModal/BaseModal';
import DiscourseCareClaimForm, {
	CareClaimDefaultFormValues,
} from './DiscourseCareClaimForm';
import {
	DefaultCareClaimsOptionsQuery,
	useLazyDefaultCareClaimsOptionsQuery,
	usePersistCareClaimsMutation,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import { useDiscourseContext } from '../../Services/DiscourseContext';

interface Props extends BaseModalProps {
	onSaveSuccess?: () => void;
	patientId: string;
	discourseId?: string;
}

const mapMedicalProceduresToOptions = (
	data: DefaultCareClaimsOptionsQuery | undefined,
) => {
	const careClaims = data?.careClaims?.data;

	if (!careClaims) {
		return undefined;
	}

	return careClaims.map(careClaim => ({
		medicalProcedure: {
			label: careClaim.medicalProcedure.formattedName,
			value: careClaim.medicalProcedure.id,
			careClaimId: careClaim?.id,
		},
	}));
};

const DiscourseCareClaimModal = ({
	onClose,
	onSaveSuccess,
	isOpen,
	patientId,
	discourseId,
	...modalProps
}: Props) => {
	const [getCareClaims, { data, isLoading }] =
		useLazyDefaultCareClaimsOptionsQuery();
	const [persistCareClaims, { isLoading: isSubmitting }] =
		usePersistCareClaimsMutation();
	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { activeDiscourseId } = useDiscourseContext();
	const targetDiscourseId = discourseId ?? activeDiscourseId;

	useEffect(() => {
		if (!isOpen || !targetDiscourseId) {
			return;
		}
		getCareClaims({
			where: {
				documentId: targetDiscourseId,
			},
		});
	}, [isOpen, targetDiscourseId, getCareClaims]);

	const onSubmit = async (data: CareClaimDefaultFormValues) => {
		if (!targetDiscourseId) {
			return;
		}

		const careClaims = data.medicalProcedures.map(item => ({
			id: item.medicalProcedure.careClaimId,
			medicalProcedureId: item.medicalProcedure.value.toString(),
			documentId: targetDiscourseId,
			patientId,
		}));

		const response = await persistCareClaims({
			where: { documentId: targetDiscourseId },
			input: { careClaims },
		});

		if ('error' in response) {
			displayGQLError(response);
			return;
		}

		showNotificationSuccess({
			content: 'component.discourse.editMedicalProceduresSuccess',
		});
		onSaveSuccess?.();
		onClose?.();
	};

	const defaultData: CareClaimDefaultFormValues = useMemo(() => {
		const careClaimsMedicalProcedures = mapMedicalProceduresToOptions(data);
		return {
			medicalProcedures: careClaimsMedicalProcedures || [
				{
					medicalProcedure: { label: '', value: '' },
				},
			],
		};
	}, [data]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<BaseModal
			{...modalProps}
			title={'component.discourse.editMedicalProcedures'}
			onClose={onClose}
			isOpen={isOpen}>
			<DiscourseCareClaimForm
				defaultData={defaultData}
				onSubmit={onSubmit}
				isLoading={isSubmitting}
			/>
		</BaseModal>
	);
};

export default DiscourseCareClaimModal;
